import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import {
  Field,
  FieldContent,
  FieldGroup,
  FieldLabel,
  FieldSet,
  FieldTitle,
} from "@/components/ui/field";
import { StepComponentProps } from "./types";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * SelectionStepComponent component allows users to select organizations to remove.
 *
 * @param form - The React Hook Form instance
 * @param participatingGroups - All participating organizations
 * @param selectedOrganizations - Currently selected organizations
 */
export function SelectionStepComponent({
  form,
  participatingGroups,
}: StepComponentProps) {
  const { t } = useTranslation(["translation", "common", "modals"]);
  const { watch, setValue, formState: { errors } } = form;
  const organizations = watch("organizations");

  const selectedOrganizationIds = organizations.map((org) => org.organizationId);

  const handleOrganizationToggle = (organizationId: number, checked: boolean) => {
    const currentOrganizations = [...organizations];
    if (checked) {
      // Add organization if not already present
      if (!selectedOrganizationIds.includes(organizationId)) {
        currentOrganizations.push({
          organizationId,
          reason: "",
        });
      }
    } else {
      // Remove organization
      const index = currentOrganizations.findIndex((org) => org.organizationId === organizationId);
      if (index > -1) {
        currentOrganizations.splice(index, 1);
      }
    }
    setValue("organizations", currentOrganizations, { shouldValidate: true });
  };

  const handleSelectAll = () => {
    if (selectedOrganizationIds.length === participatingGroups.length) {
      // Deselect all
      setValue("organizations", []);
    } else {
      // Select all
      const allOrganizations = participatingGroups.map((org) => ({
        organizationId: org.operationalOrganizationId,
        reason: "",
      }));
      setValue("organizations", allOrganizations, { shouldValidate: true });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t("modals:updateparticipantsstepperform.step1.title")}
        </h3>
        <p className="text-sm text-gray-600">
          {t("modals:updateparticipantsstepperform.step1.description")}
        </p>
      </div>

      {/* Select All/None Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="select-all"
            checked={selectedOrganizationIds.length === participatingGroups.length}
            onCheckedChange={handleSelectAll}
          />
          <label
            htmlFor="select-all"
            className="text-sm font-medium cursor-pointer"
          >
            {selectedOrganizationIds.length === participatingGroups.length
              ? t("edittender.details.selectnone")
              : t("edittender.details.selectall")}
          </label>
        </div>
        <span className="text-xs text-gray-500">
          {selectedOrganizationIds.length} / {participatingGroups.length}{" "}
          {t("edittender.details.selected")}
        </span>
      </div>

      {/* Organizations List */}
      <FieldGroup>
        <FieldSet>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {participatingGroups.map((group) => {
              const isSelected = selectedOrganizationIds.includes(
                group.operationalOrganizationId
              );
              const isPending = group.awaitingOrganizationId !== null;

              return (
                <FieldLabel
                  key={group.id}
                  htmlFor={`org-${group.operationalOrganizationId}`}
                  className="cursor-pointer"
                >
                  <Field orientation="horizontal" className="items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id={`org-${group.operationalOrganizationId}`}
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          handleOrganizationToggle(
                            group.operationalOrganizationId,
                            checked as boolean
                          )
                        }
                      />
                      <FieldContent>
                        <FieldTitle>{group.operationalOrganizationName}</FieldTitle>
                      </FieldContent>
                    </div>
                    <Badge
                      className={cn("whitespace-normal text-center",
                        isPending
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                      )}
                    >
                      {isPending
                        ? t("edittender.details.pending")
                        : t("edittender.details.bidding")}
                    </Badge>
                  </Field>
                </FieldLabel>
              );
            })}
          </div>
        </FieldSet>
      </FieldGroup>

      {errors.organizations && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {errors.organizations.message}
          </AlertTitle>
        </Alert>
      )}
    </div>
  );
}
